import {
  APP_BUILDINGS,
  BUILDING_META,
  MAX_LEVEL,
  THRESHOLDS,
  TOWN_HALL_LEAD,
  WALL,
} from "./config.ts";
import { clamp, clampInt } from "./time.ts";
import type {
  AppId,
  BuilderFlags,
  BuildingId,
  BuildingProgress,
  VillageMetrics,
} from "./types.ts";
import { APP_IDS, BUILDING_IDS } from "./types.ts";

function countThresholdLevel(value: number, thresholds: readonly number[]): number {
  let level = 0;
  for (const threshold of thresholds) {
    if (value >= threshold) {
      level += 1;
    }
  }
  return clampInt(level, 0, MAX_LEVEL);
}

function progressToward(value: number, thresholds: readonly number[], level: number): number {
  if (level >= thresholds.length) {
    return 1;
  }
  const next = thresholds[level];
  const prev = level === 0 ? 0 : thresholds[level - 1];
  if (next === undefined) {
    return 1;
  }
  const span = next - prev;
  if (span <= 0) {
    return value >= next ? 1 : 0;
  }
  return clamp((value - prev) / span, 0, 1);
}

export function townHallRawLevel(milestones: number): number {
  return countThresholdLevel(milestones, THRESHOLDS.townHall);
}

export function barracksRawLevel(metrics: VillageMetrics): number {
  if (metrics.offersReceived >= 1) {
    return THRESHOLDS.barracks.offerLevel;
  }
  if (metrics.interviewsLanded >= 1) {
    return THRESHOLDS.barracks.interviewLevel;
  }
  return countThresholdLevel(metrics.applicationsSent, THRESHOLDS.barracks.applications);
}

export function workshopRawLevel(flags: BuilderFlags): number {
  const stages = THRESHOLDS.workshop;
  let level = 0;
  for (const stage of stages) {
    if (!flags[stage]) {
      break;
    }
    level += 1;
  }
  return level;
}

export function watchtowerRawLevel(posts: number, followers: number): number {
  if (followers >= THRESHOLDS.watchtower.followers[1]) {
    return 5;
  }
  if (followers >= THRESHOLDS.watchtower.followers[0]) {
    return 4;
  }
  return countThresholdLevel(posts, THRESHOLDS.watchtower.posts);
}

export function crystalTankRawLevel(mrrUsd: number): number {
  return countThresholdLevel(mrrUsd, THRESHOLDS.crystalTank);
}

export function capByTownHall(rawLevel: number, townHallLevel: number): number {
  const cap = Math.min(MAX_LEVEL, townHallLevel + TOWN_HALL_LEAD);
  return clampInt(rawLevel, 0, cap);
}

function workshopValue(flags: BuilderFlags): string {
  const stages = THRESHOLDS.workshop;
  for (const stage of stages) {
    if (!flags[stage]) {
      return stage;
    }
  }
  return "complete";
}

function workshopNext(level: number): string | null {
  if (level >= THRESHOLDS.workshop.length) {
    return null;
  }
  return THRESHOLDS.workshop[level] ?? null;
}

function barracksValue(metrics: VillageMetrics): string {
  return `${metrics.applicationsSent} apps · ${metrics.interviewsLanded} interviews · ${metrics.offersReceived} offers`;
}

function barracksNext(rawLevel: number): string | null {
  if (rawLevel >= 5) {
    return null;
  }
  if (rawLevel >= 4) {
    return "first offer";
  }
  if (rawLevel >= 3) {
    return "first interview";
  }
  const nextApps = THRESHOLDS.barracks.applications[rawLevel];
  return nextApps === undefined ? "first interview" : `${nextApps} applications`;
}

function watchtowerValue(metrics: VillageMetrics): string {
  return `${metrics.socialPosts} posts · ${metrics.socialFollowers} followers`;
}

function watchtowerNext(rawLevel: number): string | null {
  if (rawLevel >= 5) {
    return null;
  }
  if (rawLevel >= 4) {
    return `${THRESHOLDS.watchtower.followers[1]} followers`;
  }
  if (rawLevel >= 3) {
    return `${THRESHOLDS.watchtower.followers[0]} followers`;
  }
  const nextPosts = THRESHOLDS.watchtower.posts[rawLevel];
  return nextPosts === undefined ? null : `${nextPosts} posts`;
}

function nextNumeric(level: number, thresholds: readonly number[]): number | null {
  return level >= thresholds.length ? null : (thresholds[level] ?? null);
}

function appIdForBuilding(id: BuildingId): AppId | null {
  for (const appId of APP_IDS) {
    if (APP_BUILDINGS[appId] === id) {
      return appId;
    }
  }
  return null;
}

export function rawLevelFor(id: BuildingId, metrics: VillageMetrics, wallHp: number): number {
  switch (id) {
    case "townHall":
      return townHallRawLevel(metrics.planMilestonesHit);
    case "barracks":
      return barracksRawLevel(metrics);
    case "goldMine":
      return countThresholdLevel(metrics.sideHustleGbpPerMonth, THRESHOLDS.goldMine);
    case "crystalTankRevenue":
    case "crystalTankViralz":
    case "crystalTankTikwave":
    case "crystalTankGoGrow":
    case "crystalTankRihla": {
      const appId = appIdForBuilding(id);
      return crystalTankRawLevel(appId ? metrics.appMrrUsd[appId] : 0);
    }
    case "lab":
      return countThresholdLevel(metrics.lessonsDone, THRESHOLDS.lab);
    case "workshop":
      return workshopRawLevel(metrics.builder);
    case "watchtower":
      return watchtowerRawLevel(metrics.socialPosts, metrics.socialFollowers);
    case "walls":
      return countThresholdLevel(wallHp, THRESHOLDS.wallsHpBands);
    case "builderHut":
      return countThresholdLevel(metrics.dailyActionsTotal, THRESHOLDS.builderHut);
    default: {
      const neverId: never = id;
      throw new Error(`Unhandled building: ${neverId}`);
    }
  }
}

export function describeBuilding(
  id: BuildingId,
  metrics: VillageMetrics,
  wallHp: number,
  townHallLevel: number,
): BuildingProgress {
  const rawLevel = rawLevelFor(id, metrics, wallHp);
  const level = id === "townHall" || id === "walls" ? rawLevel : capByTownHall(rawLevel, townHallLevel);

  let value: number | boolean | string = 0;
  let nextThreshold: number | string | null = null;
  let progressToNext = 1;

  switch (id) {
    case "townHall":
      value = metrics.planMilestonesHit;
      nextThreshold = nextNumeric(rawLevel, THRESHOLDS.townHall);
      progressToNext = progressToward(metrics.planMilestonesHit, THRESHOLDS.townHall, rawLevel);
      break;
    case "barracks":
      value = barracksValue(metrics);
      nextThreshold = barracksNext(rawLevel);
      if (rawLevel >= 5) {
        progressToNext = 1;
      } else if (rawLevel >= 4) {
        progressToNext = metrics.offersReceived >= 1 ? 1 : 0;
      } else if (rawLevel >= 3) {
        progressToNext = metrics.interviewsLanded >= 1 ? 1 : 0;
      } else {
        progressToNext = progressToward(
          metrics.applicationsSent,
          THRESHOLDS.barracks.applications,
          rawLevel,
        );
      }
      break;
    case "goldMine":
      value = metrics.sideHustleGbpPerMonth;
      nextThreshold = nextNumeric(rawLevel, THRESHOLDS.goldMine);
      progressToNext = progressToward(metrics.sideHustleGbpPerMonth, THRESHOLDS.goldMine, rawLevel);
      break;
    case "crystalTankRevenue":
    case "crystalTankViralz":
    case "crystalTankTikwave":
    case "crystalTankGoGrow":
    case "crystalTankRihla": {
      const appId = appIdForBuilding(id);
      const mrr = appId ? metrics.appMrrUsd[appId] : 0;
      value = mrr;
      nextThreshold = nextNumeric(rawLevel, THRESHOLDS.crystalTank);
      progressToNext = progressToward(mrr, THRESHOLDS.crystalTank, rawLevel);
      break;
    }
    case "lab":
      value = metrics.lessonsDone;
      nextThreshold = nextNumeric(rawLevel, THRESHOLDS.lab);
      progressToNext = progressToward(metrics.lessonsDone, THRESHOLDS.lab, rawLevel);
      break;
    case "workshop":
      value = workshopValue(metrics.builder);
      nextThreshold = workshopNext(rawLevel);
      progressToNext = rawLevel >= MAX_LEVEL ? 1 : 0;
      break;
    case "watchtower":
      value = watchtowerValue(metrics);
      nextThreshold = watchtowerNext(rawLevel);
      if (rawLevel >= 5) {
        progressToNext = 1;
      } else if (rawLevel >= 4) {
        progressToNext = progressToward(
          metrics.socialFollowers,
          THRESHOLDS.watchtower.followers,
          1,
        );
      } else if (rawLevel >= 3) {
        progressToNext = progressToward(
          metrics.socialFollowers,
          THRESHOLDS.watchtower.followers,
          0,
        );
      } else {
        progressToNext = progressToward(metrics.socialPosts, THRESHOLDS.watchtower.posts, rawLevel);
      }
      break;
    case "walls":
      value = wallHp;
      nextThreshold = nextNumeric(rawLevel, THRESHOLDS.wallsHpBands);
      progressToNext = progressToward(wallHp, THRESHOLDS.wallsHpBands, rawLevel);
      break;
    case "builderHut":
      value = metrics.dailyActionsTotal;
      nextThreshold = nextNumeric(rawLevel, THRESHOLDS.builderHut);
      progressToNext = progressToward(metrics.dailyActionsTotal, THRESHOLDS.builderHut, rawLevel);
      break;
    default: {
      const neverId: never = id;
      throw new Error(`Unhandled building: ${neverId}`);
    }
  }

  return {
    id,
    level,
    rawLevel,
    capped: level < rawLevel,
    value,
    nextThreshold,
    progressToNext,
  };
}

export function computeLevels(
  metrics: VillageMetrics,
  wallHp: number,
): { levels: Record<BuildingId, number>; buildings: BuildingProgress[] } {
  const townHallLevel = townHallRawLevel(metrics.planMilestonesHit);
  const buildings = BUILDING_IDS.map((id) => describeBuilding(id, metrics, wallHp, townHallLevel));
  const levels = {} as Record<BuildingId, number>;
  for (const building of buildings) {
    levels[building.id] = building.level;
  }
  return { levels, buildings };
}

export function emptyBuilderFlags(): BuilderFlags {
  return {
    repoMade: false,
    firstDeploy: false,
    mvpLive: false,
    firstUser: false,
    firstPayingUser: false,
  };
}

export function emptyAppMrr(): Record<AppId, number> {
  return {
    revenue: 0,
    viralz: 0,
    tikwave: 0,
    gogrow: 0,
    rihla: 0,
  };
}

export function formatMetricValue(id: BuildingId, value: number | boolean | string): string {
  const kind = BUILDING_META[id].valueKind;
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "boolean") {
    return value ? "yes" : "no";
  }
  switch (kind) {
    case "gbp":
      return `£${value}`;
    case "usd":
      return `$${value}`;
    case "hp":
      return `${value} / ${WALL.maxHp} HP`;
    case "count":
    case "stage":
    case "mixed":
      return String(value);
    default: {
      const neverKind: never = kind;
      throw new Error(`Unhandled value kind: ${neverKind}`);
    }
  }
}
